import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { CardOpener, Icon, IconButton, Stack, Text } from '@ui'
import { useErrorToast } from 'hooks/useErrorToast'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import { Address, useSetPrimaryWallet, useUnlinkWalletTransaction } from 'use-towns-client'

import { mapToErrorMessage } from '../utils'

export function UnlinkPopup({
    isAbstractAccount,
    setIsUnlinkPopupOpen,
    address,
    isPrimaryWallet,
    userId,
}: {
    isAbstractAccount: boolean
    setIsUnlinkPopupOpen: (isOpen: boolean) => void
    address: Address
    isPrimaryWallet: boolean
    userId: string
}) {
    const { unlinkWalletTransaction, error: errorUnlinkWallet } = useUnlinkWalletTransaction()
    const { execute: setPrimaryWallet, isLoading: isSettingPrimaryWallet } = useSetPrimaryWallet({
        onSuccess: ({ address }) => {
            popupToast(({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message={`Primary wallet set to ${shortAddress(address)}`}
                />
            ))
        },
        onError: (error) => {
            popupToast(({ toast }) => (
                <StandardToast.Error toast={toast} message="Failed to set primary wallet" />
            ))
        },
    })

    useErrorToast({
        errorMessage: errorUnlinkWallet
            ? mapToErrorMessage({
                  error: errorUnlinkWallet,
                  source: 'wallet linking unlink wallet',
              })
            : undefined,
    })

    const primaryWalletIsAbstractAccount = isAbstractAccount && isPrimaryWallet

    const onUnlinkClick = async (addressToUnlink: Address, getSigner: GetSigner) => {
        const signer = await getSigner()
        if (!signer) {
            createPrivyNotAuthenticatedNotification()
            return
        }
        if (isPrimaryWallet) {
            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="This is your primary wallet."
                    subMessage="Please designate another primary wallet before unlinking."
                />
            ))
            return
        }
        unlinkWalletTransaction(signer, addressToUnlink)
    }

    const Row = ({
        onClick,
        Text,
        Icon,
    }: {
        onClick: () => void
        Text: React.ReactNode
        Icon: React.ReactNode
    }) => {
        return (
            <Stack
                horizontal
                gap="sm"
                alignItems="center"
                as="button"
                background="none"
                cursor="pointer"
                onClick={onClick}
            >
                <Stack>
                    <Stack
                        centerContent
                        width="x4"
                        aspectRatio="square"
                        background="level3"
                        rounded="xs"
                    >
                        {Icon}
                    </Stack>
                </Stack>
                {Text}
            </Stack>
        )
    }

    return (
        <CardOpener
            placement="dropdown"
            render={
                <WalletReady
                    LoginButton={
                        <Stack
                            horizontal
                            background="level2"
                            padding="md"
                            rounded="md"
                            gap="sm"
                            alignItems="center"
                            boxShadow="card"
                            border="level3"
                            width="x30"
                        >
                            <Text size="sm">
                                Please re-authenticate with Privy to unlink a wallet.
                            </Text>
                        </Stack>
                    }
                >
                    {({ getSigner }) => (
                        <Stack
                            padding="md"
                            background="level2"
                            rounded="md"
                            width="x21"
                            boxShadow="card"
                            border="level3"
                            gap="md"
                        >
                            {primaryWalletIsAbstractAccount ? (
                                <Text size="sm">This wallet cannot be unlinked.</Text>
                            ) : isPrimaryWallet ? (
                                <Text size="sm">
                                    Please select another primary wallet before unlinking.
                                </Text>
                            ) : (
                                <>
                                    <Row
                                        Icon={<Icon type="star" color="gray2" size="square_sm" />}
                                        Text={
                                            <Text color="default" size="sm">
                                                Set as primary
                                            </Text>
                                        }
                                        onClick={async () => {
                                            const signer = await getSigner()
                                            if (!signer) {
                                                createPrivyNotAuthenticatedNotification()
                                                return
                                            }
                                            setPrimaryWallet({
                                                userId,
                                                signer,
                                                type: isAbstractAccount ? 'smartAccount' : 'eoa',
                                                address,
                                            })
                                        }}
                                    />
                                    {isAbstractAccount ? null : (
                                        <Row
                                            Icon={
                                                <Icon
                                                    type="unlink"
                                                    color="negative"
                                                    size="square_sm"
                                                />
                                            }
                                            Text={
                                                <Text color="negative" size="sm">
                                                    Unlink
                                                </Text>
                                            }
                                            onClick={() => onUnlinkClick(address, getSigner)}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    )}
                </WalletReady>
            }
            onClose={() => setIsUnlinkPopupOpen(false)}
        >
            {({ triggerProps }) =>
                isSettingPrimaryWallet ? (
                    <Stack padding="sm">
                        <ButtonSpinner />
                    </Stack>
                ) : (
                    <Stack
                        {...triggerProps}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            triggerProps.onClick?.(e)
                            setIsUnlinkPopupOpen(true)
                        }}
                    >
                        <IconButton
                            icon="ellipsis"
                            size="square_md"
                            color="gray2"
                            padding="xs"
                            background="level2"
                        />
                    </Stack>
                )
            }
        </CardOpener>
    )
}
